import { useState, useRef, useCallback } from "react";
import { BsDot } from "react-icons/bs";
import { FiX, FiEdit2, FiCheck, FiXCircle } from "react-icons/fi";
import * as XLSX from "xlsx";
import Papa from "papaparse";
import { CardComp } from "../cardComp";
import { ButtonComp } from "../Ui";
import { ReusableFileUploaderComp } from "../Ui/reusable-file-uploader";
import { TableCompData } from "../Table";
import { useAuth } from "../../hooks";
import toast from "react-hot-toast";

const categoryHeaderRegex = /^(.+)\s+\((\d+)\)\s*$/;

/** Excel / sheet serial or string → YYYY-MM-DD when possible */
function normalizeCutoverDate(value) {
  if (value == null || value === "") return "";
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const y = value.getFullYear();
    const m = String(value.getMonth() + 1).padStart(2, "0");
    const d = String(value.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }
  if (typeof value === "number" && value > 20000 && value < 100000) {
    const epoch = Date.UTC(1899, 11, 30);
    const ms = epoch + Math.round(value) * 86400000;
    const dt = new Date(ms);
    if (!Number.isNaN(dt.getTime())) {
      const y = dt.getUTCFullYear();
      const m = String(dt.getUTCMonth() + 1).padStart(2, "0");
      const d = String(dt.getUTCDate()).padStart(2, "0");
      return `${y}-${m}-${d}`;
    }
  }
  let s = String(value).trim().replace(/^["']|["']$/g, "");
  // Y-m-d with trailing time / ISO (backend was throwing Carbon Trailing data)
  const isoPrefix = s.match(/^(\d{4}-\d{2}-\d{2})(?:[ T].*)?$/);
  if (isoPrefix) return isoPrefix[1];
  // d/m/Y or d-m-Y (Nigeria-first)
  const dmy = s.match(/^(\d{1,2})[\/\-.](\d{1,2})[\/\-.](\d{4})$/);
  if (dmy) {
    const day = Number(dmy[1]);
    const month = Number(dmy[2]);
    const year = Number(dmy[3]);
    if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
      return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    }
  }
  return s;
}

function extractCategoryColumns(keys) {
  const cols = [];
  const seen = new Set();
  for (const key of keys) {
    const m = String(key).trim().match(categoryHeaderRegex);
    if (m) {
      const id = parseInt(m[2], 10);
      if (!Number.isNaN(id) && !seen.has(id)) {
        seen.add(id);
        cols.push({ id, headerKey: key, label: String(key).trim() });
      }
    }
  }
  return cols.sort((a, b) => a.id - b.id);
}

function mergeFieldErrors(...sources) {
  const out = {};
  for (const src of sources) {
    if (!src) continue;
    Object.entries(src).forEach(([k, msgs]) => {
      if (!msgs?.length) return;
      out[k] = [...(out[k] || []), ...msgs];
    });
  }
  return out;
}

function buildRowPayload(row, categoryColumns) {
  const category_balances = {};
  categoryColumns.forEach(({ id, headerKey }) => {
    const raw = row[headerKey];
    if (raw === undefined || raw === null || raw === "") {
      category_balances[id] = null;
      return;
    }
    const n = Number(String(raw).replace(/,/g, ""));
    category_balances[id] = Number.isFinite(n) ? n : raw;
  });
  return {
    first_name: row.first_name ?? "",
    last_name: row.last_name ?? "",
    email: row.email ?? "",
    phone: row.phone ?? "",
    address: row.address ?? "",
    cutover_date: normalizeCutoverDate(row.cutover_date) || "",
    category_balances,
  };
}

export const MemberBatchUploadComp = () => {
  const [loading, setLoading] = useState(false);
  const [parsedData, setParsedData] = useState([]);
  const [categoryColumns, setCategoryColumns] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});
  const [editingRow, setEditingRow] = useState(null);
  const [editedData, setEditedData] = useState({});
  const token = useAuth();
  const fileInputRef = useRef(null);

  const baseUrl = process.env.REACT_APP_BASE_URL;

  const isValidEmail = (email) => {
    if (!email) return false;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email).trim());
  };

  const isValidPhone = (phone) => {
    if (!phone) return false;
    return String(phone).replace(/\D/g, "").length === 11;
  };

  /** @returns {Record<string, string[]>} */
  const validateRowFieldsLocal = (row, index, allRows) => {
    const fields = {};
    if (!row.first_name?.trim()) fields.first_name = ["First name is required"];
    if (!row.last_name?.trim()) fields.last_name = ["Last name is required"];
    if (row.email?.trim()) {
      if (!isValidEmail(row.email)) fields.email = [...(fields.email || []), "Invalid email format"];
    }

    if (!row.phone) fields.phone = [...(fields.phone || []), "Phone number is required"];
    else if (!isValidPhone(row.phone)) fields.phone = [...(fields.phone || []), "Phone must be 11 digits"];

    if (row.next_of_kin_phone && !isValidPhone(row.next_of_kin_phone)) {
      fields.next_of_kin_phone = ["Next of kin phone must be 11 digits"];
    }

    const emailLower = row.email?.trim()?.toLowerCase();
    if (emailLower) {
      const dup = allRows.filter(
        (r, i) => r.email?.trim()?.toLowerCase() === emailLower && i !== index
      ).length;
      if (dup > 0) fields.email = [...(fields.email || []), "Duplicate email in file"];
    }

    const phoneDigits = String(row.phone || "").replace(/\D/g, "");
    if (phoneDigits) {
      const dupP = allRows.filter(
        (r, i) => String(r.phone || "").replace(/\D/g, "") === phoneDigits && i !== index
      ).length;
      if (dupP > 0) fields.phone = [...(fields.phone || []), "Duplicate phone in file"];
    }

    if (!row.cutover_date?.toString().trim()) {
      fields.cutover_date = ["Cutover date is required (YYYY-MM-DD)"];
    } else {
      const c = normalizeCutoverDate(row.cutover_date);
      if (!/^\d{4}-\d{2}-\d{2}$/.test(c)) {
        fields.cutover_date = ["Invalid cutover date"];
      }
    }

    categoryColumns.forEach(({ id, headerKey }) => {
      const key = `category_${id}`;
      const raw = row[headerKey];
      if (raw === undefined || raw === null || raw === "") return;
      const n = Number(String(raw).replace(/,/g, ""));
      if (!Number.isFinite(n)) fields[key] = ["Must be a number"];
      else if (n < 0) fields[key] = ["Cannot be negative"];
    });

    return fields;
  };

  const runServerValidation = useCallback(
    async (rows, cols) => {
      if (!token || !rows.length) return {};
      const payloadRows = rows.map((row) => {
        const r = { ...row, cutover_date: normalizeCutoverDate(row.cutover_date) };
        return buildRowPayload(r, cols);
      });
      try {
        const res = await fetch(
          `${baseUrl}/cooperatives/member_managements/batch/validate?guard=staff`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              Accept: "application/json",
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ rows: payloadRows }),
          }
        );
        const json = await res.json().catch(() => ({}));
        if (!res.ok || json.status === false) {
          toast.error(json.message || "Validation request failed");
          return {};
        }
        const serverRows = json.data?.rows || [];
        const map = {};
        serverRows.forEach((item) => {
          const idx = item.index;
          if (idx != null && item.fields && Object.keys(item.fields).length) {
            map[idx] = item.fields;
          }
        });
        return map;
      } catch (e) {
        console.error(e);
        toast.error("Could not reach server for validation");
        return {};
      }
    },
    [baseUrl, token]
  );

  const recomputeErrors = useCallback(
    async (data, cols) => {
      const local = {};
      data.forEach((row, index) => {
        const fe = validateRowFieldsLocal(row, index, data);
        if (Object.keys(fe).length) local[index] = fe;
      });
      const serverMap = await runServerValidation(data, cols);
      const merged = {};
      data.forEach((_, index) => {
        merged[index] = mergeFieldErrors(local[index], serverMap[index]);
      });
      setFieldErrors(merged);
    },
    [runServerValidation]
  );

  const parseFile = (file) => {
    return new Promise((resolve, reject) => {
      const fileExtension = file.name.split(".").pop().toLowerCase();

      if (fileExtension === "csv") {
        Papa.parse(file, {
          header: true,
          skipEmptyLines: true,
          complete: (results) => resolve(results.data),
          error: reject,
        });
      } else if (["xlsx", "xls"].includes(fileExtension)) {
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const data = new Uint8Array(e.target.result);
            const workbook = XLSX.read(data, { type: "array", cellDates: true });
            const firstSheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[firstSheetName];
            const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: "" });
            resolve(jsonData);
          } catch (error) {
            reject(error);
          }
        };
        reader.onerror = reject;
        reader.readAsArrayBuffer(file);
      } else {
        reject(new Error("Unsupported file format. Please upload CSV or Excel file."));
      }
    });
  };

  const normalizeRowFromFile = (row) => {
    const normalized = { ...row };
    Object.keys(row).forEach((key) => {
      const lowerKey = key.toLowerCase().trim().replace(/\s+/g, "_");
      if (lowerKey === "first_name" || lowerKey === "firstname" || (lowerKey.includes("first") && lowerKey.includes("name") && !lowerKey.includes("kin"))) {
        normalized.first_name = row[key];
      }
      if (lowerKey === "last_name" || lowerKey === "lastname" || (lowerKey.includes("last") && lowerKey.includes("name"))) {
        normalized.last_name = row[key];
      }
      if (lowerKey === "email" || lowerKey.includes("email")) {
        normalized.email = row[key];
      }
      if ((lowerKey === "phone" || lowerKey === "phone_number") && !lowerKey.includes("kin")) {
        normalized.phone = row[key];
      }
      if (lowerKey === "address" || lowerKey.includes("address")) {
        normalized.address = row[key];
      }
      if (lowerKey === "cutover_date" || lowerKey === "cutoverdate" || (lowerKey.includes("cutover") && lowerKey.includes("date"))) {
        normalized.cutover_date = normalizeCutoverDate(row[key]);
      }
    });
    return normalized;
  };

  const handleFileUpload = async (selectedFile) => {
    if (!selectedFile) return;
    setLoading(true);
    try {
      const data = await parseFile(selectedFile);
      if (!data || data.length === 0) {
        toast.error("The uploaded file is empty or contains no data");
        setLoading(false);
        return;
      }

      const firstKeys = Object.keys(data[0] || {});
      const cols = extractCategoryColumns(firstKeys);
      if (cols.length === 0) {
        toast.error(
          "No savings columns found. Download the latest template — category columns must look like: Name (12)."
        );
        setLoading(false);
        return;
      }

      const normalizedData = data.map((row, index) => {
        const n = normalizeRowFromFile(row);
        n.cutover_date = normalizeCutoverDate(n.cutover_date);
        return { ...n, _originalIndex: index };
      });

      setCategoryColumns(cols);
      setParsedData(normalizedData);
      await recomputeErrors(normalizedData, cols);
      toast.success(`Parsed ${normalizedData.length} row(s). Review cells (green = ok, red = error).`);
    } catch (error) {
      console.error(error);
      toast.error(error.message || "Failed to parse file.");
    }
    setLoading(false);
  };

  const handleRemoveRow = (index) => {
    const newData = parsedData.filter((_, i) => i !== index);
    setParsedData(newData);
    recomputeErrors(newData, categoryColumns);
    setEditingRow(null);
    setEditedData({});
    toast.success("Row removed");
  };

  const handleStartEdit = (index) => {
    setEditingRow(index);
    setEditedData({ ...parsedData[index] });
  };

  const handleCancelEdit = () => {
    setEditingRow(null);
    setEditedData({});
  };

  const handleSaveEdit = (index) => {
    const updatedData = [...parsedData];
    updatedData[index] = { ...editedData };
    setParsedData(updatedData);
    recomputeErrors(updatedData, categoryColumns);
    setEditingRow(null);
    setEditedData({});
    toast.success("Row updated");
  };

  const handleEditChange = (field, value) => {
    setEditedData((prev) => ({ ...prev, [field]: value }));
  };

  const rowHasErrors = (index) => Object.keys(fieldErrors[index] || {}).length > 0;

  const validRowsCount = parsedData.reduce(
    (n, _, i) => (rowHasErrors(i) ? n : n + 1),
    0
  );

  const handleDownloadTemplate = async () => {
    if (!token) {
      toast.error("Please sign in again");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(
        `${baseUrl}/cooperatives/member_managements/batch/download?guard=staff`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept:
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/json",
          },
        }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.message || "Failed to download template");
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `member-batch-upload-template-${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Template downloaded");
    } catch (e) {
      console.error(e);
      toast.error("Download failed");
    }
    setLoading(false);
  };

  const handleStartNewUpload = () => {
    setParsedData([]);
    setFieldErrors({});
    setCategoryColumns([]);
    setEditingRow(null);
    setEditedData({});
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleFinalize = async () => {
    const validIndices = parsedData.map((_, i) => i).filter((i) => !rowHasErrors(i));
    if (validIndices.length === 0) {
      toast.error("No valid rows to import. Fix errors first.");
      return;
    }
    setLoading(true);
    try {
      const rows = validIndices.map((i) => {
        const row = parsedData[i];
        const r = { ...row, cutover_date: normalizeCutoverDate(row.cutover_date) };
        return buildRowPayload(r, categoryColumns);
      });
      const res = await fetch(
        `${baseUrl}/cooperatives/member_managements/batch/commit?guard=staff`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ rows }),
        }
      );
      const json = await res.json().catch(() => ({}));
      setLoading(false);
      if (!res.ok || json.status === false) {
        toast.error(json.message || "Import failed");
        return;
      }
      const failed = json.data?.failed || [];
      const succeeded = json.data?.succeeded?.length ?? 0;
      if (failed.length) {
        const summary =
          succeeded > 0
            ? `${json.message || "Import completed with some failures"} (${succeeded} imported, ${failed.length} failed)`
            : json.message || "Import failed";
        toast.error(
          <div className="text-start" style={{ maxWidth: 420 }}>
            <div className="fw-semibold mb-2">{summary}</div>
            <ul className="small mb-0 ps-3" style={{ listStyleType: "disc" }}>
              {failed.map((f) => (
                <li key={`fail-${f.index}`}>
                  <span className="fw-medium">Row {Number(f.index) + 1}:</span> {f.message}
                </li>
              ))}
            </ul>
          </div>,
          { duration: 12000 }
        );
        setFieldErrors((prev) => {
          const next = { ...prev };
          failed.forEach((f) => {
            const idx = f.index;
            next[idx] = mergeFieldErrors(next[idx] || {}, {
              _import: [f.message || "Import failed"],
            });
          });
          return next;
        });
      } else {
        toast.success(json.message || `Successfully imported ${succeeded} member(s)`);
        setParsedData([]);
        setFieldErrors({});
        setCategoryColumns([]);
        setEditingRow(null);
        setEditedData({});
        if (fileInputRef.current) fileInputRef.current.value = "";
      }
    } catch (e) {
      console.error(e);
      toast.error(e.message || "Import failed");
      setLoading(false);
    }
  };

  const cellWrap = (fieldKey, rowIndex, content) => {
    const errs = fieldErrors[rowIndex]?.[fieldKey];
    const ok = !errs?.length && content !== "" && content != null;
    const hasErr = errs?.length > 0;
    return (
      <div
        className="px-1 py-1 rounded"
        style={{
          border: `2px solid ${hasErr ? "#ff4343" : ok ? "#19bc31" : "#e0e0e0"}`,
          minHeight: 36,
          backgroundColor: hasErr ? "#fff5f5" : ok ? "#f6fff8" : "transparent",
        }}
        title={hasErr ? errs.join("; ") : ""}
      >
        {content}
        {hasErr ? (
          <div className="text-danger small mt-1">{errs[0]}</div>
        ) : null}
      </div>
    );
  };

  const getPreviewColumns = () => {
    const cols = [
      {
        name: "Row",
        selector: (_, index) => index + 1,
        width: "56px",
        sortable: false,
      },
      {
        name: "First Name",
        selector: (row, index) => {
          const fk = "first_name";
          if (editingRow === index) {
            return cellWrap(
              fk,
              index,
              <input
                type="text"
                className="form-control form-control-sm"
                value={editedData.first_name || ""}
                onChange={(e) => handleEditChange("first_name", e.target.value)}
              />
            );
          }
          return cellWrap(fk, index, row.first_name || "—");
        },
        sortable: false,
      },
      {
        name: "Last Name",
        selector: (row, index) => {
          const fk = "last_name";
          if (editingRow === index) {
            return cellWrap(
              fk,
              index,
              <input
                type="text"
                className="form-control form-control-sm"
                value={editedData.last_name || ""}
                onChange={(e) => handleEditChange("last_name", e.target.value)}
              />
            );
          }
          return cellWrap(fk, index, row.last_name || "—");
        },
        sortable: false,
      },
      {
        name: "Email",
        selector: (row, index) => {
          const fk = "email";
          if (editingRow === index) {
            return cellWrap(
              fk,
              index,
              <input
                type="email"
                className="form-control form-control-sm"
                value={editedData.email || ""}
                onChange={(e) => handleEditChange("email", e.target.value)}
              />
            );
          }
          return cellWrap(fk, index, row.email || "—");
        },
        sortable: false,
      },
      {
        name: "Phone",
        selector: (row, index) => {
          const fk = "phone";
          if (editingRow === index) {
            return cellWrap(
              fk,
              index,
              <input
                type="text"
                className="form-control form-control-sm"
                value={editedData.phone || ""}
                onChange={(e) => handleEditChange("phone", e.target.value)}
              />
            );
          }
          return cellWrap(fk, index, row.phone || "—");
        },
        sortable: false,
      },
      {
        name: "Address",
        selector: (row, index) => {
          if (editingRow === index) {
            return (
              <input
                type="text"
                className="form-control form-control-sm"
                value={editedData.address || ""}
                onChange={(e) => handleEditChange("address", e.target.value)}
              />
            );
          }
          return row.address || "—";
        },
        sortable: false,
      },
      {
        name: "Cutover date",
        selector: (row, index) => {
          const fk = "cutover_date";
          if (editingRow === index) {
            return cellWrap(
              fk,
              index,
              <input
                type="date"
                className="form-control form-control-sm"
                value={
                  normalizeCutoverDate(editedData.cutover_date) &&
                  /^\d{4}-\d{2}-\d{2}$/.test(normalizeCutoverDate(editedData.cutover_date))
                    ? normalizeCutoverDate(editedData.cutover_date)
                    : ""
                }
                onChange={(e) => handleEditChange("cutover_date", e.target.value)}
              />
            );
          }
          return cellWrap(fk, index, normalizeCutoverDate(row.cutover_date) || "—");
        },
        sortable: false,
      },
    ];

    categoryColumns.forEach(({ id, headerKey, label }) => {
      const fk = `category_${id}`;
      cols.push({
        name: label.length > 40 ? `${label.slice(0, 37)}…` : label,
        selector: (row, index) => {
          if (editingRow === index) {
            return cellWrap(
              fk,
              index,
              <input
                type="text"
                className="form-control form-control-sm"
                value={editedData[headerKey] ?? ""}
                onChange={(e) => handleEditChange(headerKey, e.target.value)}
              />
            );
          }
          const v = row[headerKey];
          const display = v === "" || v == null ? "0" : String(v);
          return cellWrap(fk, index, display);
        },
        sortable: false,
        minWidth: "140px",
      });
    });

    cols.push({
      name: "Row status",
      selector: (row, index) => {
        const importErr = fieldErrors[index]?._import?.[0];
        if (importErr) {
          return (
            <div className="small">
              <span className="text-danger fw-semibold d-block">Import failed</span>
              <span className="text-danger" style={{ wordBreak: "break-word" }}>
                {importErr}
              </span>
            </div>
          );
        }
        return rowHasErrors(index) ? (
          <span className="text-danger fw-semibold">Errors</span>
        ) : (
          <span className="text-success fw-semibold">Ready</span>
        );
      },
      minWidth: "140px",
      sortable: false,
    });

    cols.push({
      name: "Actions",
      selector: (row, index) => {
        if (editingRow === index) {
          return (
            <div className="d-flex gap-2">
              <button type="button" className="btn btn-sm btn-success" onClick={() => handleSaveEdit(index)} title="Save">
                <FiCheck size={16} color="#19bc31" />
              </button>
              <button type="button" className="btn btn-sm btn-secondary" onClick={handleCancelEdit} title="Cancel">
                <FiXCircle size={16} color="#6c757d" />
              </button>
            </div>
          );
        }
        return (
          <div className="d-flex gap-2">
            <button type="button" className="btn btn-sm btn-primary" onClick={() => handleStartEdit(index)} title="Edit">
              <FiEdit2 size={16} color="var(--color-primary)" />
            </button>
            <button type="button" className="btn btn-sm btn-danger" onClick={() => handleRemoveRow(index)} title="Remove">
              <FiX size={16} color="#ff4343" />
            </button>
          </div>
        );
      },
      width: "120px",
      sortable: false,
    });

    return cols;
  };

  const conditionalRowStyles = [
    {
      when: (row, index) => rowHasErrors(index),
      style: { backgroundColor: "#fff8f8" },
    },
  ];

  const invalidRowsCount = parsedData.length - validRowsCount;

  return (
    <>
      <section className="mx-lg-4 min-vh-100">
        <CardComp cardClassName={"border-0 px-lg-4 mb-3"}>
          <div className="d-flex justify-content-between align-items-center">
            <h4 className="fw-bold mb-0 text-black1">Batch Member Upload</h4>
            <div>
              <button
                type="button"
                className="px-4 bg-new-primary rounded text-white h-10 border-0 hover:scale-105"
                onClick={handleDownloadTemplate}
                disabled={loading}
              >
                Download Template
              </button>
            </div>
          </div>
        </CardComp>

        <CardComp cardClassName={"border-0 px-lg-4 mb-3 pt-4"}>
          <div className="row g-5 mb-4 justify-content-between">
            <div className="col-lg-5">
              {parsedData.length === 0 ? (
                <>
                  <div className="col-lg-12 border-lg-end mb-4">
                    <ReusableFileUploaderComp
                      handleUpload={handleFileUpload}
                      loading={loading}
                      accept={{
                        "text/csv": [".csv"],
                        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
                        "application/vnd.ms-excel": [".xls"],
                      }}
                      bodyText={
                        <div className="py-5 w-100 rounded" style={{ background: "#FAFAFA" }}>
                          <div className="py-2" />
                          <p className="text-center text-black fw-bold">Click or Drag & Drop CSV/Excel file here</p>
                          <div className="py-2" />
                        </div>
                      }
                    />
                  </div>

                  <button
                    type="button"
                    className="col-lg-12 border-lg-end mb-5 border text-center h-10 flex items-center justify-center rounded border-[#A1A0A0] bg-[#FAFAFA] hover:scale-105 cursor-pointer text-black select-none px-4"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={loading}
                  >
                    Browse Files
                  </button>
                </>
              ) : (
                <div
                  className="d-flex align-items-center justify-content-between border rounded px-3 py-2 mb-3 bg-white"
                  style={{ minHeight: 56 }}
                >
                  <div className="small text-muted mb-0">
                    Current file loaded.
                  </div>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary"
                    onClick={handleStartNewUpload}
                    disabled={loading}
                  >
                    Upload new file
                  </button>
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx,.xls"
                style={{ display: "none" }}
                onChange={(e) => {
                  if (e.target.files?.[0]) handleFileUpload(e.target.files[0]);
                }}
              />
            </div>
            {parsedData.length === 0 && (
              <div className="col-lg-6 ps-lg-5">
                <CardComp cardClassName="border-0 shadow py-3">
                  <h5 className="text-black mb-4 fw-bold text-center">Upload Instructions</h5>
                  <div className="d-flex align-items-start mb-3">
                    <span>
                      <BsDot color="var(--color-primary)" size={40} />
                    </span>
                    <p className="mb-0">
                      Download the <strong>template</strong> for your branch. It includes one column per active savings
                      category (name and ID in the header) plus <strong>cutover_date</strong> per row.
                    </p>
                  </div>
                  <div className="d-flex align-items-start mb-3">
                    <span>
                      <BsDot color="var(--color-primary)" size={40} />
                    </span>
                    <p className="mb-0">
                      Required: <strong>First name</strong>, <strong>Last name</strong>, <strong>Phone</strong> (11 digits),
                      and <strong>cutover_date</strong> (YYYY-MM-DD) for each member. Example: <strong>2026-09-12</strong>.{" "}
                      <strong>Email</strong> is optional; if provided it must be valid and unique within the file.{" "}
                      Empty savings cells are treated as <strong>0</strong> (audited, no transaction).
                    </p>
                  </div>
                  <div className="d-flex align-items-start mb-3">
                    <span>
                      <BsDot color="var(--color-primary)" size={40} />
                    </span>
                    <p className="mb-0">
                      After upload, the table validates locally and with the server. Cells show a <span className="text-success">green</span>{" "}
                      border when valid or <span className="text-danger">red</span> when there is an error.
                    </p>
                  </div>
                  <div className="d-flex align-items-start mb-0">
                    <span>
                      <BsDot color="var(--color-primary)" size={40} />
                    </span>
                    <p className="mb-0">
                      Click <strong>Finalize import</strong> to save only rows without errors. Opening balances appear in member
                      transaction history as &quot;Opening balance import&quot;.
                    </p>
                  </div>
                </CardComp>
              </div>
            )}
          </div>
        </CardComp>

        {parsedData.length > 0 && (
          <CardComp cardClassName={"border-0 px-lg-4 mb-3"}>
            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
              <div>
                <h5 className="fw-bold text-black1 mb-1">Data Preview & Validation</h5>
                <p className="text-muted small mb-0">
                  Total: {parsedData.length} row(s) | Ready:{" "}
                  <span className="text-success fw-bold">{validRowsCount}</span> | With errors:{" "}
                  <span className="text-danger fw-bold">{invalidRowsCount}</span>
                </p>
              </div>
              {validRowsCount > 0 && (
                <ButtonComp
                  onClick={handleFinalize}
                  btnText={loading ? "Importing…" : `Finalize import (${validRowsCount})`}
                  btnClassName="text-white bg-primary1 border-0 py-2 px-4 h6 rounded"
                  disabled={loading}
                />
              )}
            </div>
            <div className="alert alert-info mb-3">
              <small>
                <strong>Note:</strong> Only rows marked <strong>Ready</strong> are imported. Fix red cells or remove bad
                rows. Savings amounts &gt; 0 create an opening-balance transaction; 0 is recorded on the audit log only.
              </small>
            </div>
            <TableCompData
              columns={getPreviewColumns()}
              data={parsedData}
              pagination={parsedData.length > 10}
              conditionalRowStyles={conditionalRowStyles}
              marginBottom="0"
            />
          </CardComp>
        )}
      </section>
    </>
  );
};
